import type { AnySensorKey, ReelRunGraphKey } from "@/base/sensors"
import type {
  ChartDatum,
  DateChangeHandler,
  MinDateMaxDate,
} from "@/base/types"
import type { PayloadAction, Reducer } from "@reduxjs/toolkit"

import * as AppStorage from "@/base/async-storage"
import { loadFieldIrrigationHistoryAsync, loadFieldIrrigationHeatmapAsync, setReelRunApplicationTypeAsync, setReelRunDirectionAsync, setReelRunFieldAsync } from "@/base/farmhq-api"
import * as Geo from "@/base/geo"
import {
  convertSensorValue,
  requiresConversion,
} from "@/base/sensor-conversions"
import { formatSensorValue } from "@/base/sensor-formatting"
import { isNumberValid, isTruthyString } from "@/base/type-guards"
import { formatDateSafely } from "@/base/utility"
import { useErrorHandler } from "@/hooks/useErrorHandler"
import * as Models from "@/redux/models"
import { constructDate } from "@/theme/time-utils"
import i18n from "@/translations/i18n"
import { createSelector, createSlice, isAnyOf } from "@reduxjs/toolkit"
import * as dateFunctions from "date-fns"
import i18next from "i18next"
import _ from "lodash"
import { createCachedSelector } from "re-reselect"

import type { DeviceConfiguration } from "./device-configurations.reducer"
import type { RootState } from "./root.reducer"

import { signOutAsync } from "./auth.reducer"
import {
  calculateReelRunApplication,
  getSwathGeometry,
} from "./reel-runs.reducer"
import { getFetchStatusByName } from "./requests.reducer"
import {
  getActiveFarmLat,
  getActiveFarmLatLngFromState,
  getActiveFarmLng,
  getUserMeasurementPreferenceFromState,
} from "./selectors"

import { COLORS, useThemedValue } from "@/theme"
import React from "react"
import { createStructuredSelector } from "reselect"
import { useRootDispatch } from "./useRootDispatch"
import { useRootSelector } from "./useRootSelector"

export interface HeatMapSettings {
  contrast: "default" | "high"
}

export type IrrigationMapMode = "runs" | "density"

interface FieldIrrigationHistoryState
  extends Models.ModelState<ReelRunHistorical>,
    MinDateMaxDate {
  graphKey: ReelRunGraphKey
  heatmapGrid: Models.IrrigationHeatmapGrid | null
  heatmapSettings: HeatMapSettings
  isAutozoomEnabled?: boolean
  isSettingsOpen: boolean
  mapMode: IrrigationMapMode
  selectedRunId?: number
  showOutlines?: boolean
  tempDirectionOverride?: {
    reelRunId: number
    direction: number
  }
  // When true the backend recomputes application rate using the nozzle
  // diameter from the device-configuration snapshot tied to each run instead
  // of the current configuration. Only meaningful for runs that don't have a
  // flow rate override; the toggle is hidden when every visible run does.
  useHistoricalNozzle: boolean
}

const initialState: FieldIrrigationHistoryState = {
  dateMsMax: dateFunctions.subDays(new Date(), 365).getTime(),
  dateMsMin: Date.now(),
  entities: {},
  graphKey: "applicationRateReportsMm",
  heatmapGrid: null,
  heatmapSettings: {
    contrast: AppStorage.STORAGE_DEFAULTS.runHeatmapContrast,
  },
  ids: [],
  isSettingsOpen: false,
  mapMode: "density",
  useHistoricalNozzle: false,
  ...(__DEV__
    ? {
        // selectedRunId: 4,
      }
    : {}),
}

const adapter = Models.fieldIrrigationHistoryRuns.adapter
function prepareReelRun(reelRun: Models.ReelRun) {
  return {
    ...reelRun,
    ...calculateReelRunApplication(reelRun),
  }
}
export type ReelRunHistorical = ReturnType<typeof prepareReelRun>

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(loadFieldIrrigationHistoryAsync.pending, (state) => {
        adapter.removeAll(state)
      })
      .addCase(
        loadFieldIrrigationHistoryAsync.fulfilled,
        (state, { payload }) => {
          if (!__DEV__) {
            state.selectedRunId = undefined
          }
          adapter.setAll(state, payload.items.map(prepareReelRun))
        },
      )
      .addCase(
        AppStorage.readLocalStorageAsync.fulfilled,
        (state, { payload }) => {
          // MIN DATE
          state.dateMsMin = payload.fieldIrrigationHistoryDateMin
          // MAX DATE
          state.dateMsMax = payload.fieldIrrigationHistoryDateMax
          state.showOutlines = payload.showReelRunOutline
          state.isAutozoomEnabled = payload.fieldRunHistoryAutozoomEnabled
          state.heatmapSettings = {
            contrast: payload.runHeatmapContrast,
          }
        },
      )
      .addCase(AppStorage.setRunHeatmapContrast.pending, (state, action) => {
        state.heatmapSettings.contrast = action.meta.arg
      })
      .addCase(AppStorage.setFieldProfileAutoZoom.pending, (state, action) => {
        state.isAutozoomEnabled = action.meta.arg
      })
      .addCase(AppStorage.setShowRunOutlines.pending, (state, action) => {
        state.showOutlines = action.meta.arg
      })
      .addCase(
        AppStorage.setIrrigationHistoryDateMin.pending,
        (state, action) => {
          // MIN DATE
          state.dateMsMin = action.meta.arg
        },
      )
      .addCase(
        AppStorage.setIrrigationHistoryDateMax.pending,
        (state, action) => {
          // MAX DATE
          state.dateMsMax = action.meta.arg
        },
      )
      .addCase(loadFieldIrrigationHeatmapAsync.pending, (state) => {
        state.heatmapGrid = null
      })
      .addCase(
        loadFieldIrrigationHeatmapAsync.fulfilled,
        (state, { payload }) => {
          state.heatmapGrid = payload
        },
      )
      .addCase(setReelRunDirectionAsync.fulfilled, (state, { meta }) => {
        // Update the specific reel run with the new direction override
        const { reelRunId, value } = meta.arg
        const existingRun = state.entities[reelRunId]
        if (existingRun) {
          existingRun.directionOverrideAzimuthDegrees = value
        }
        // Clear temporary direction override since the change is now persisted
        state.tempDirectionOverride = undefined
      })
      .addCase(setReelRunDirectionAsync.rejected, () => {
        // Keep temporary direction override on error so user can retry
      })
      .addCase(setReelRunApplicationTypeAsync.fulfilled, (state, { meta }) => {
        const { reelRunId, value } = meta.arg
        const existingRun = state.entities[reelRunId]
        if (existingRun) {
          existingRun.applicationType = value
        }
      })
      .addCase(setReelRunFieldAsync.fulfilled, (state, { meta }) => {
        const { fieldId, reelRunId } = meta.arg
        const existingRun = state.entities[reelRunId]
        if (!existingRun) {
          return
        }
        // Every run in this store belongs to the field being viewed, so a
        // run pinned to a different field leaves the list. Clearing to
        // automatic keeps the run until the next reload resolves it.
        if (typeof fieldId === "number" && fieldId !== existingRun.fieldId) {
          adapter.removeOne(state, reelRunId)
          if (state.selectedRunId === reelRunId) {
            state.selectedRunId = undefined
          }
        } else {
          existingRun.fieldOverrideId = fieldId
        }
      })
      .addMatcher(isAnyOf(signOutAsync.fulfilled), () => {
        return { ...initialState }
      }),
  initialState,
  name: "fieldIrrigationHistory",
  reducers: {
    clearSelectedReelRunHistorical: (state) => {
      state.selectedRunId = undefined
      state.tempDirectionOverride = undefined
    },
    selectReelRunHistorical: (state, { payload }: PayloadAction<number>) => {
      state.selectedRunId = payload
      state.tempDirectionOverride = undefined
    },
    setHeatmapContrast: (
      state,
      { payload }: PayloadAction<HeatMapSettings["contrast"]>,
    ) => {
      state.heatmapSettings = { ...state.heatmapSettings, contrast: payload }
    },
    setReelRunGraphKey: (
      state,
      { payload }: PayloadAction<ReelRunGraphKey>,
    ) => {
      state.graphKey = payload
    },
    setTempDirectionOverride: (
      state,
      { payload }: PayloadAction<{ reelRunId: number; direction: number }>,
    ) => {
      state.tempDirectionOverride = payload
    },
    clearTempDirectionOverride: (state) => {
      state.tempDirectionOverride = undefined
    },
    toggleUiSettingsMenu: (state) => {
      if (state.isSettingsOpen) {
        state.isSettingsOpen = false
      } else {
        state.isSettingsOpen = true
      }
    },
    setUseHistoricalNozzle: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.useHistoricalNozzle = payload
    },
    setIrrigationMapMode: (
      state,
      { payload }: PayloadAction<IrrigationMapMode>,
    ) => {
      state.mapMode = payload
    },
  },
})

const fieldIrrigationHistory: Reducer<FieldIrrigationHistoryState> =
  slice.reducer

export default fieldIrrigationHistory

export const {
  clearSelectedReelRunHistorical,
  selectReelRunHistorical,
  setHeatmapContrast,
  setIrrigationMapMode,
  setReelRunGraphKey,
  setTempDirectionOverride,
  clearTempDirectionOverride,
  toggleUiSettingsMenu,
  setUseHistoricalNozzle,
} = slice.actions

export function getUseHistoricalNozzleFromState(state: RootState) {
  return state.fieldIrrigationHistory.useHistoricalNozzle
}

export function getAnyRunUsesNozzleFallback(state: RootState): boolean {
  const entities = state.fieldIrrigationHistory.entities
  for (const id of state.fieldIrrigationHistory.ids) {
    const run = entities[id]
    if (run && run.calculationMethod !== "flow_rate_override") {
      return true
    }
  }
  return false
}

export function getReelRunGraphKeyFromState(state: RootState) {
  return state.fieldIrrigationHistory.graphKey
}
export function getSelectedFieldIrrigationHistoryRunId(state: RootState) {
  return state.fieldIrrigationHistory.selectedRunId
}

export function getFieldIrrigationHistoryDateMsMax(state: RootState) {
  return state.fieldIrrigationHistory.dateMsMax
}

export function getFieldIrrigationHistoryDateMsMin(state: RootState) {
  return state.fieldIrrigationHistory.dateMsMin
}

export function getIsLoadingRuns(state: RootState) {
  return (
    getFetchStatusByName(state, "LoadFieldIrrigationHistory") === "pending"
  )
}
export const getFieldIrrigationHistoryDates = createStructuredSelector({
  dateMsMax: getFieldIrrigationHistoryDateMsMax,
  dateMsMin: getFieldIrrigationHistoryDateMsMin,
})

export function useFieldIrrigationHistoryDatePicker(key: "max" | "min") {
  const dispatch = useRootDispatch()
  const handleError = useErrorHandler()
  const committedMs = useRootSelector(
    key === "min"
      ? getFieldIrrigationHistoryDateMsMin
      : getFieldIrrigationHistoryDateMsMax,
  )

  // Hold the in-progress date while the user edits the picker. We only write
  // it to the store on commit (calendar close) — writing on every change
  // refetches the whole history on each keystroke and disables the input
  // mid-typing (see getIsLoadingRuns / useLoadFieldIrrigationHistory, which
  // fetches whenever these store values change).
  const draftMsRef = React.useRef<number | null>(null)

  const onChange: DateChangeHandler = React.useCallback((value) => {
    const asDate = constructDate(value)
    if (asDate) {
      draftMsRef.current = asDate.getTime()
    }
  }, [])

  const onCommit = React.useCallback(() => {
    const nextMs = draftMsRef.current
    draftMsRef.current = null
    if (nextMs === null || nextMs === committedMs) {
      return
    }
    const action =
      key === "min"
        ? AppStorage.setIrrigationHistoryDateMin
        : AppStorage.setIrrigationHistoryDateMax
    dispatch(action(nextMs))
      .unwrap()
      .catch((error) =>
        handleError(error, {
          toastMessage: "default",
        }),
      )
  }, [committedMs, dispatch, handleError, key])

  return { onChange, onCommit }
}

export const getTotalApplicationForSelectedField = createSelector(
  [Models.fieldIrrigationHistoryRuns.selectAll],
  function calculateTotalApplicationForReelRuns(runs) {
    let result = 0
    for (const run of runs) {
      const { applicationAcreInchesTotal } = calculateReelRunApplication(run)

      if (isNumberValid(applicationAcreInchesTotal)) {
        result += applicationAcreInchesTotal
      }
    }
    return Geo.round(result, 2)
  },
)

export const calculateMetricsForSelectedRunMemo = createSelector(
  [
    getUserMeasurementPreferenceFromState,
    getSelectedFieldIrrigationHistoryRunId,
    Models.fieldIrrigationHistoryRuns.selectEntities,
  ],
  function calculateMetricsForSelectedRun(
    measurementPreference,
    runId,
    entities,
  ) {
    if (!isNumberValid(runId)) {
      return null
    }
    const runData = entities[runId]
    if (!runData) {
      return null
    }
    const speedMmpmAverage = _.mean(runData.speedReportsMmpm)
    if (typeof runData.startTimestamp === "string") {
      const application = calculateReelRunApplication({
        applicationRateReportsMm: runData.applicationRateReportsMm,
        distanceMmCurrent: runData.distanceMmCurrent,
        distanceMmMax: runData.distanceMmMax,
        distanceReportsMm: runData.distanceReportsMm,
        reelSwathWidthMm: runData.reelSwathWidthMm,
      })
      const psiAverage = Array.isArray(runData.pressureReportsKpa)
        ? _.mean(runData.pressureReportsKpa)
        : undefined
      const applicationRateMmAverage = Geo.round(
        application.applicationRateMmAverage,
        2,
      )
      const applicationAcreInchesTotal = application.applicationAcreInchesTotal

      const formatValue = (
        fieldName: AnySensorKey,
        rawValue: number | undefined,
      ): string | undefined =>
        formatSensorValue({ fieldName, measurementPreference, rawValue })

      const start = new Date(runData.startTimestamp)

      let duration: dateFunctions.Duration | undefined
      if (isTruthyString(runData.endTimestamp)) {
        const end = new Date(runData.endTimestamp)
        duration = dateFunctions.intervalToDuration({ end, start })
      }

      return {
        applicationAverage: i18n.t("averageApplicationRateWithVal", {
          ns: "fieldIrrigationHistory",
          val: formatValue(
            "applicationRateEstimatedMm",
            applicationRateMmAverage,
          ),
        }),
        applicationTotal: isNumberValid(applicationAcreInchesTotal)
          ? i18n.t("totalApplicationWithVal", {
              ns: "fieldIrrigationHistory",
              val: applicationAcreInchesTotal,
            })
          : undefined,
        psiAverage: isNumberValid(psiAverage)
          ? i18n.t("averagePressureWithVal", {
              ns: "fieldIrrigationHistory",
              val: formatValue("readingKpa", psiAverage),
            })
          : undefined,
        runDuration: i18n.t("durationWithVal", {
          ns: "fieldIrrigationHistory",

          val: duration
            ? dateFunctions.formatDuration(duration, {
                format: ["hours", "minutes"],
              })
            : i18n.t("ongoing", { ns: "common" }),
        }),
        speedAverage: i18n.t("averageSpeedWithVal", {
          ns: "fieldIrrigationHistory",
          val: formatValue("runSpeedMmpm", speedMmpmAverage),
        }),
        startDate: i18n.t("startDateWithVal", {
          ns: "fieldIrrigationHistory",
          val: dateFunctions.format(start, "Pp"),
        }),
        calculationMethodLabel: runData.calculationMethod
          ? i18n.t(`calculationMethod.${runData.calculationMethod}`, {
              ns: "fieldIrrigationHistory",
            })
          : undefined,
        applicationTypeLabel: runData.applicationType
          ? i18n.t(`applicationTypeWithVal`, {
              ns: "fieldIrrigationHistory",
              val: i18n.t(`applicationType.${runData.applicationType}`, {
                ns: "fieldIrrigationHistory",
              }),
            })
          : undefined,
      }
    }
    return undefined
  },
)

export interface ReelRunGraphProps extends MinDateMaxDate {
  maxValue: number
  values: ChartDatum[]
  xAxisTickValues: number[]
  yAxisFieldName: ReelRunGraphKey
}

export const getReelRunGraphDataByIdFromStateCached = createCachedSelector(
  Models.fieldIrrigationHistoryRuns.selectById,
  getUserMeasurementPreferenceFromState,
  getReelRunGraphKeyFromState,
  function getReelRunGraphData(runInfo, measurementPreference, graphKey) {
    if (!runInfo) {
      return undefined
    }
    let maxValue: number
    switch (graphKey) {
      case "applicationRateReportsMm": {
        maxValue = 5
        break
      }
      case "pressureReportsKpa": {
        maxValue = 250
        break
      }
      case "speedReportsMmpm": {
        maxValue = 250
        break
      }
    }

    const startTimestamp = isTruthyString(runInfo.startTimestamp)
      ? new Date(runInfo.startTimestamp).getTime()
      : dateFunctions.subHours(new Date(), 8).getTime()

    const endTimestamp = isTruthyString(runInfo.endTimestamp)
      ? new Date(runInfo.endTimestamp).getTime()
      : Date.now()

    const runIntervalsMilliseconds = _.range(
      startTimestamp,
      endTimestamp,
      60 * 60 * 1000,
    )

    const initializer: ReelRunGraphProps = {
      dateMsMax: endTimestamp,
      dateMsMin: startTimestamp,
      maxValue,
      values: [],
      xAxisTickValues: runIntervalsMilliseconds,
      yAxisFieldName: graphKey,
    }
    const valuesList = runInfo[graphKey] ?? []

    return valuesList.reduce(function generateGraphValues(
      acc,
      rawValue,
      index,
    ): ReelRunGraphProps {
      let timestamp = runInfo.deviceEventTimestamps[index]
      if (
        typeof runInfo.endTimestamp === "string" &&
        typeof runInfo.startTimestamp === "string"
      ) {
        const startMilliseconds = new Date(runInfo.startTimestamp).getTime()
        const endMilliseconds = new Date(runInfo.endTimestamp).getTime()

        const runLengthMinutes =
          (endMilliseconds - startMilliseconds) / 1000 / 60

        const spacing = runLengthMinutes / valuesList.length

        if (typeof timestamp === "undefined") {
          let fallbackTimestamp: Date
          if (index === 0) {
            fallbackTimestamp = new Date(runInfo.startTimestamp)
          } else {
            fallbackTimestamp = dateFunctions.addMinutes(
              new Date(runInfo.startTimestamp),
              spacing * index,
            )
          }
          timestamp = fallbackTimestamp.toISOString()
        }
      }
      if (isNumberValid(rawValue) && typeof timestamp === "string") {
        const value = requiresConversion(graphKey)
          ? convertSensorValue({
              fieldName: graphKey,
              measurementPreference,
              rawValue,
              target: "user",
            })
          : rawValue
        if (value > acc.maxValue) {
          acc.maxValue = value
        }
        const milliseconds = new Date(timestamp).getTime()
        return {
          ...acc,
          values: [...acc.values, { x: milliseconds, y: value }],
        }
      }
      return acc
    }, initializer)
  },
)(
  (state, id: number | null | undefined) =>
    `${id ?? "undefined"}/${getReelRunGraphKeyFromState(state)}`,
)

export function generateFieldRunHistoryItem({
  configuration,
  farmLocation,
  runData,
}: {
  configuration: DeviceConfiguration | null | undefined
  farmLocation: Geo.PointInput | null | undefined
  runData: Models.ReelRun
}) {
  let text = formatDateSafely(runData.startTimestamp)
  if (Boolean(runData.endTimestamp)) {
    const endTimestampText = formatDateSafely(runData.endTimestamp, "p")
    if (typeof endTimestampText === "string") {
      text += ` - ${endTimestampText}`
    }
  }
  // const configuration = configurations[runData.deviceId ?? ""]
  const application = calculateReelRunApplication(runData)

  const deviceName =
    configuration?.deviceName ??
    configuration?.codaDeviceAlias ??
    i18n.t("unknown")
  return {
    ...getSwathGeometry({ ...runData, farmLocation }),
    ...application,
    applicationText:
      typeof application.applicationAcreInchesTotal === "number"
        ? `${application.applicationAcreInchesTotal} ${i18next.t("acreInches")}`
        : undefined,
    applicationType: runData.applicationType ?? null,
    calculationMethod: runData.calculationMethod ?? null,
    deviceName,
    endTimestamp: runData.endTimestamp,
    reelRunId: runData.reelRunId,
    reelSwathWidthMm: runData.reelSwathWidthMm,
    startTimestamp: runData.startTimestamp,
    text,
    textPrimary: dateFunctions.formatDate(
      new Date(runData.startTimestamp),
      "Pp",
    ),
  }
}

export interface IrrigationHistoryItemType
  extends ReturnType<typeof generateFieldRunHistoryItem> {
  index: number
  text: string
}

export const getFieldIrrigationHistoryRunsMemo = createSelector(
  Models.fieldIrrigationHistoryRuns.selectAll,
  Models.deviceConfiguration.selectEntities,
  getActiveFarmLng,
  getActiveFarmLat,
  (state: RootState) => state.fieldIrrigationHistory.tempDirectionOverride,
  function getFieldIrrigationHistoryRunsMemo(
    runs,
    configurations,
    farmLng,
    farmLat,
    tempDirectionOverride,
  ) {
    const farmLocation = Geo.point([farmLng, farmLat])
    return runs.map((runData) => {
      const configuration =
        typeof runData.deviceId === "string"
          ? configurations[runData.deviceId]
          : undefined
      
      // Apply temporary direction override if it exists for this run
      const effectiveRunData = tempDirectionOverride?.reelRunId === runData.reelRunId
        ? {
            ...runData,
            extendHeadingDegrees: tempDirectionOverride.direction,
          }
        : runData
      
      return generateFieldRunHistoryItem({
        configuration,
        farmLocation,
        runData: effectiveRunData,
      })
    })
  },
)
export function getReelRunIndexByRunIdFromState(
  state: RootState,
  runId: number | undefined,
) {
  return Models.fieldIrrigationHistoryRuns
    .selectAll(state)
    .findIndex((reelRun) => reelRun.reelRunId === runId)
}

export const REEL_RUN_GRAPH_LINE_COLOR = {
  dark: COLORS.$darkBlue[600],
  light: COLORS.$darkBlue[600],
}
export function useReelRunGraphLineColor() {
  return useThemedValue(REEL_RUN_GRAPH_LINE_COLOR)
}

export function useSelectIrrigationHistoryRun() {
  const dispatch = useRootDispatch()
  return ({ reelRunId }: { reelRunId: number }) => {
    dispatch(selectReelRunHistorical(reelRunId))
  }
}

export function getFieldIrrigationHistoryMapMode(state: RootState): IrrigationMapMode {
  return state.fieldIrrigationHistory.mapMode
}

export function getFieldIrrigationHeatmapGrid(
  state: RootState,
): Models.IrrigationHeatmapGrid | null {
  return state.fieldIrrigationHistory.heatmapGrid
}

export function getIsHeatmapLoading(state: RootState): boolean {
  return getFetchStatusByName(state, "LoadFieldIrrigationHeatmap") === "pending"
}

export const getReelRunWithGeometryFromStateCached = createSelector(
  [Models.fieldIrrigationHistoryRuns.selectById, getActiveFarmLatLngFromState],
  (run, farmLocation) => {
    if (run) {
      return {
        ...run,
        ...getSwathGeometry({
          farmLocation: farmLocation?.native,
          ...run,
        }),
      }
    }
    return undefined
  },
)
